import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  PAGE_ROUTE_BLOG
} from "@kivunova/kivufrontendcommon";
import { ABOUT_FEATURES } from "./constants/aboutPageContent";
import { AboutFeatureId } from "./enums";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  publicInfoBodySx,
  publicInfoFeatureCardSx,
  publicInfoFeatureIconSx,
  publicInfoLinkSx,
  publicInfoSectionPanelSx,
  publicInfoSectionStackSx,
  publicInfoSectionTitleSx
} from "./constants/publicInfoPageStyles";

const ABOUT_FEATURE_ICONS = {
  [AboutFeatureId.INVENTORY]: Inventory2OutlinedIcon,
  [AboutFeatureId.ORDERS]: ShoppingCartOutlinedIcon,
  [AboutFeatureId.STORES]: StorefrontOutlinedIcon,
  [AboutFeatureId.INSIGHTS]: InsightsOutlinedIcon,
  [AboutFeatureId.APPS]: GetAppOutlinedIcon
} as const;

const AboutPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const learnMorePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_BLOG, language, defaultLang)
      : `/${PAGE_ROUTE_BLOG}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="about-page"
        eyebrow={t("about-page-eyebrow", "Built for growing businesses")}
        title={t("about-page-title", "About Kivunova FMS")}
        intro={t(
          "about-page-intro",
          "Kivunova FMS is a Financial and Inventory Management System that helps businesses run day-to-day operations from one secure, multilingual workspace."
        )}
      />

      <Stack sx={publicInfoSectionStackSx}>
        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("about-page-mission-heading", "Our mission")}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "about-page-mission-body",
              "We simplify how teams track products, fulfill orders, manage stores, and understand performance, so owners and staff spend less time on manual records and more time growing the business."
            )}
          </Typography>
        </Box>

        <Box>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("about-page-features-heading", "What you can do")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)"
              },
              gap: { xs: 1.5, sm: 2 }
            }}
          >
            {ABOUT_FEATURES.map((feature) => {
              const Icon = ABOUT_FEATURE_ICONS[feature.id];
              return (
                <Box key={feature.id} sx={publicInfoFeatureCardSx}>
                  <Box sx={publicInfoFeatureIconSx}>
                    <Icon fontSize="small" />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ ...publicInfoBodySx, color: "text.primary", m: 0 }}
                  >
                    {t(feature.titleKey, feature.titleFallback)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("about-page-built-heading", "Built for Rwanda and beyond")}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "about-page-built-body",
              "Kivunova is designed for local businesses, with support in several languages, easy company registration, and features that grow from one shop to many branches."
            )}
          </Typography>
        </Box>

        <PublicInfoCtaPanel
          title={t("about-page-cta-title", "Want the full picture?")}
          testId="about-page-cta"
        >
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "about-page-cta-body",
              "Explore how Kivunova FMS helps teams manage stock, sales, money, and growth, or follow the User guide for step-by-step help."
            )}{" "}
            <MuiLink
              component={Link}
              to={learnMorePath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t(
                "about-page-cta-learn-more-link",
                "Learn more about Kivunova FMS"
              )}
            </MuiLink>
          </Typography>
        </PublicInfoCtaPanel>
      </Stack>
    </PublicMarketingLayout>
  );
};

export default AboutPage;
